<template>
<div id="library">

  <!-- Public folder upload warning -->
  <div v-if="folderAccess.access === 'public'" class="public-upload-warning">
    <span class="icon-earth" />
    You are viewing a <b>PUBLIC</b> folder. Any file uploaded here is downloadable by anyone on the internet.
  </div>

  <!-- File Uploader -->
  <div class="mt-1"><FileUploader :path="folderPath" @upload="initialize()" style="min-height: 100px;" /></div>


  <section class="flex-row flex-align-start flex-justify-start flex-gap-1 mt-2">

    <!-- File List -->
    <div v-if="!loading && !error" id="media-list-container" class="half">
      <MediaLibraryList :items="mediaLibrary" :iconName="'icon-file-empy'" :folderPath="folderPath" :subfolders="subfolders" :folderAccess="folderAccess" :subfolderAccess="subfolderAccess" :publicFiles="publicFiles" :selectNewest="selectNewest" :update="updateList" @select="f => selectItem( f )" @renameItem="(oldName, newName) => renameItem( oldName, newName)" @delete="deleteItemPopup" @deleteItems="items => deleteSelectedItems( items )" @selectFolder="f => selectFolder( f )" @createFolder="name => createFolder( name )" @deleteFolder="name => deleteFolder( name )" @saveAccess="(access, grants, done) => saveAccess( access, grants, done )" @setPublicFiles="filenames => setPublicFiles( filenames )" @goto="idx => gotoFolder(idx)" />
    </div>
    <div v-else-if="error">There was an error fetching the media library</div>
    <div v-else-if="loading" class="center">Loading...</div>


    <!-- Previews -->
    <div v-if="selectedFile" class="sticky-top" style="max-width: min( 800px, 40vw );">

      <div v-if="selectedMediaType == 'image'" style="max-width: 50vw;">
        <img :src="getURL(selectedFile)" style="max-width: 100%;" />
      </div>

      <div v-else-if="selectedMediaType == 'video'">
        <video v-if="getURL(selectedFile)" controls controlslist="nodownload" style="object-fit: contain; max-width: min( 800px, 40vw );" ref="video">
          <source type="video/mp4" ref="videoSource">
        </video>
        <p v-else>No preview</p>
      </div>

      <div v-else-if="selectedMediaType == 'text'">
        <div v-if="textPreviewLoading" class="center">Loading…</div>
        <div v-else-if="textPreviewError">Unable to load file contents.</div>
        <pre v-else class="text-preview">{{ textData }}</pre>
      </div>

      <div><a :href="getURL(selectedFile)" target="_blank">{{ getURL(selectedFile) }}</a></div>

      <div class="center mt-3"><button v-if="Selecting" class="button font-size-1-3" style="padding-left: 2em; padding-right: 2em;" @click="assignFile">Assign File</button></div>
    </div>
  </section>


  <ConfirmDialog confirm="Delete File" ref="confirmDeleteModal" @confirm="deleteItem()">
    <div>Are you sure you want to delete this file?</div>
    <div class="bold font-size-1-3">This action CAN NOT be undone!</div>
  </ConfirmDialog>

</div>
</template>

<script>
import MediaLibraryList from "@/components/MediaLibraryList.vue";
import FileUploader from '@/features/FileUploader/FileUploader.vue'
import ConfirmDialog from '@/components/utilities/ConfirmDialog.vue'

import MediaLibraryAPI from "@/api/MediaLibraryAPI.js";
import { base_url } from "@/Config";


export default {
  name: "media-library",


  components: {
    MediaLibraryList,
    FileUploader,
    ConfirmDialog,
  },


  props: {
    Selecting: Boolean
  },



  data() {
    return {
      mediaLibrary: [],
      subfolders: [],

      folderAccess: { access: 'private', grants: [], managed: false },
      subfolderAccess: {},
      publicFiles: [],

      itemToDelete: null,
      loading: false,
      error: false,

      folderPath: [],
      mediaTypeString: '',
      textData: "",
      textPreviewLoading: false,
      textPreviewError: false,
      // config: this.$store.state.courseConfig,
      selectNewest: 0,
      selectedFile: null,
      selectedFilename: "",
      selectedMediaType: null,
      mediaLocations: [],
      updateList: 0,

    }
  },



  created() { this.initialize() },



  methods: {

    async initialize() {

      this.loading = true
      this.error = false
      this.selectedFile = null

      try {
        const promises = []
        promises.push( MediaLibraryAPI.getMedia( this.folderPath ).then( res => this.mediaLibrary = res.data ) )
        promises.push( this.loadSubfolders() )
        promises.push( this.loadAccess() )

        await Promise.all( promises )

      } catch ( e ) {
        console.error( e.message )
        this.error = true

      } finally {
        this.loading = false
      }
    },

    async loadSubfolders() {
      return MediaLibraryAPI.getSubfolders( this.folderPath ).then( res => this.subfolders = res.data )
    },


    async loadAccess() {
      const [ folder, children ] = await Promise.all([
        MediaLibraryAPI.getFolderAccess( this.folderPath ),
        MediaLibraryAPI.getSubfolderAccess( this.folderPath ),
      ])

      const data = folder && folder.data ? folder.data : folder
      this.folderAccess = {
        access: data && data.access === 'public' ? 'public' : 'private',
        grants: data && Array.isArray(data.grants) ? data.grants : [],
        managed: data ? data.managed : false
      }
      this.publicFiles = data && Array.isArray( data.publicFiles ) ? data.publicFiles : []

      this.subfolderAccess = ( children && children.data ) ? children.data : ( children || {} )
    },


    // Full-list replace of the folder's per-file public overrides. The server
    // response is authoritative (it scrubs/de-dupes), so re-render from it.
    async setPublicFiles( filenames ) {
      try {
        const res = await MediaLibraryAPI.setPublicFiles( this.folderPath, filenames )
        const data = res && res.data ? res.data : res
        this.publicFiles = data && Array.isArray( data.publicFiles ) ? data.publicFiles : []

      } catch ( e ) {
        console.error( e )
        alert( "Error updating public files. Changes not saved." )
      }
    },


    async deleteSelectedItems( items ) {
      if( !items || !items.length ) return

      try {
        for( const item of items ) {
          await MediaLibraryAPI.deleteMedia( this.folderPath, item.filename )
        }
        this.selectItem()
        await this.initialize()
        this.updateList++

      } catch ( e ) {
        console.error( e )
        alert( "Error communicating with server. Changes not saved." )
      }
    },


    async saveAccess( access, grants, done ) {
      try {
        await MediaLibraryAPI.setFolderAccess( this.folderPath, access, grants )
        this.folderAccess = { access, grants }
        if( typeof done === 'function' ) done()
        // Reload the whole folder: writing access settings may change the
        // folder's dotfile, which can affect the file/subfolder listing.
        await this.initialize()

      } catch ( e ) {
        console.error( e )
        alert( "Error saving folder access. Changes not saved." )
      }
    },


    gotoFolder( idx ) {
      if( idx + 1 >= this.folderPath.length ) return

      while( this.folderPath.length > idx + 1 ) this.folderPath.pop()
      this.initialize()
    },


    selectFolder( folderName ) {
      if( folderName == '..' ) {
        if( this.folderPath.length == 0 ) return
        this.folderPath.pop()

      } else this.folderPath.push( folderName )

      this.initialize()
    },


    async createFolder( folderName ) {
      const path = []
      for( let f of this.folderPath ) path.push( f )
      path.push( folderName )

      await MediaLibraryAPI.createFolder( path ).catch( err => alert( `Failed to create folder ${folderName}:\n${err.message}` ) )
      this.initialize()
    },


    async deleteFolder( folderName ) {
      const path = []
      for( let f of this.folderPath ) path.push( f )
      path.push( folderName )

      const res = await MediaLibraryAPI.deleteFolder( path ).catch( err => alert( `Failed to delete folder ${folderName}:\n${err.message}` ) )
      if( res.data ) alert( `Failed to delete folder ${folderName}:\n${res.data}` )
      this.initialize()

    },



    selectMediaType(type) {
      this.selectedFile = null;
      this.selectedFilename = '';
    },

    selectItem(file) {
      if(file) this.selectedFilename = file.filename;
      else this.selectedFilename = '';
      this.selectedFile = file;
      this.selectedMediaType = this.getSelectedMediaType( file )

      if( this.selectedMediaType == 'text' ) this.loadTextPreview( file )
    },


    getSelectedMediaType( file ) {
      if( !file || !file.filename ) return null

      const fn = file.filename.toLowerCase()
      if( fn.endsWith('.png') || fn.endsWith('.jpg') || fn.endsWith('.jpeg') || fn.endsWith('.gif') || fn.endsWith('.jxl') ) return 'image'
      if( fn.endsWith('.mp4') || fn.endsWith('.m4v') || fn.endsWith('.mkv') || fn.endsWith('.webm') || fn.endsWith('.mov') ) return 'video'

      // Dotfiles (e.g. the .private / .public access dotfile) and common text formats
      if( fn.startsWith('.') ) return 'text'
      if( fn.endsWith('.txt') || fn.endsWith('.text') || fn.endsWith('.md') || fn.endsWith('.json')
        || fn.endsWith('.csv') || fn.endsWith('.log') || fn.endsWith('.xml') || fn.endsWith('.yml')
        || fn.endsWith('.yaml') || fn.endsWith('.html') || fn.endsWith('.htm') || fn.endsWith('.css')
        || fn.endsWith('.js') || fn.endsWith('.svg') ) return 'text'

      return null
    },


    async loadTextPreview( file ) {
      this.textData = ''
      this.textPreviewError = false
      this.textPreviewLoading = true

      try {
        const res = await fetch( this.getURL( file ), { credentials: 'include' } )
        if( !res.ok ) throw new Error( `${res.status} response fetching file` )
        this.textData = await res.text()

      } catch ( e ) {
        console.error( 'Failed to load text preview:', e.message )
        this.textPreviewError = true

      } finally {
        this.textPreviewLoading = false
      }
    },


    getURL( file ) {
      const subfolderPath = this.folderPath.join('/') + ( this.folderPath.length > 0 ? '/' : '' )
      const src = `${base_url}/webAssets/content/site_wide_media/${subfolderPath}${file.filename}`

      if( this.selectedMediaType == 'video' ) {
        this.$nextTick( () => {
          this.$refs.videoSource.setAttribute( 'src', src )
          this.$refs.video.load()
        })
      }

      return src
    },


    // If a file is changed, it will need to be renamed in each screen
    async renameItem( oldName, newName ) {

      await MediaLibraryAPI.renameMedia(this.folderPath, oldName, newName)
      .catch( err => {
        alert("Error while renaming file.");
      })

      this.initialize()
    },


    deleteItemPopup( item ) {
      this.itemToDelete = item
      this.$refs.confirmDeleteModal.open()
    },


    async deleteItem() {

      try {
        await MediaLibraryAPI.deleteMedia(this.folderPath, this.itemToDelete.filename);
        this.selectItem();
        await this.initialize()
        this.updateList++;

      } catch (e) {
        console.error(e);
        alert("Error communicating with server. Changes not saved.");

      } finally {
        this.itemToDelete = null
      }
    },


    async fileUploaded() {
      await this.initialize()
    },

    assignFile() {
      this.$emit('chosen', this.selectedFile);
    }
  },
}
</script>


<style scoped>
.text-preview {
  white-space: pre-wrap;
  word-break: break-word;
  font-family: monospace;
  font-size: 0.85em;
  background-color: #f6f6f6;
  border: 1px solid #ddd;
  border-radius: 0.25em;
  padding: 0.75em 1em;
  max-height: 60vh;
  overflow: auto;
}

.public-upload-warning {
  display: flex;
  align-items: center;
  gap: 0.5em;
  background-color: var(--pp10-orange-90);
  border-left: 4px solid var(--pp10-orange);
  color: var(--pp10-orange-30);
  padding: 0.6em 1em;
  border-radius: 0.25em;
  margin-bottom: 0.25em;
  font-weight: bold;
}
</style>
